import type { FaqEntryDto, FaqUpsert } from "@care-by-ella/contracts";
import { API_BASE, apiFetch, tenantHeader, withCredentials } from "./api";
import { jsonOr401 } from "./clinician-api";

// TES-214 — FAQ authoring client.

function commonHeaders(): Record<string, string> {
  return { "Content-Type": "application/json", ...tenantHeader() };
}

export class FaqApiError extends Error {}

async function jsonOrError<T>(res: Response): Promise<T> {
  if (res.status === 401 || res.status === 403) return jsonOr401<T>(res);
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { message?: unknown; error?: unknown };
      const msg = body.message ?? body.error;
      if (typeof msg === "string" && msg.trim()) detail = msg;
    } catch {
      // keep status-code detail
    }
    throw new FaqApiError(detail);
  }
  return (await res.json()) as T;
}

const BASE = `${API_BASE}/employee-api/v1/faq`;

export async function listFaq(): Promise<FaqEntryDto[]> {
  const res = await apiFetch(BASE, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function createFaq(body: FaqUpsert): Promise<FaqEntryDto> {
  const res = await apiFetch(BASE, {
    method: "POST",
    headers: commonHeaders(),
    body: JSON.stringify(body),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function updateFaq(
  id: string,
  body: FaqUpsert,
): Promise<FaqEntryDto> {
  const res = await apiFetch(`${BASE}/${id}`, {
    method: "PUT",
    headers: commonHeaders(),
    body: JSON.stringify(body),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function deleteFaq(id: string): Promise<void> {
  const res = await apiFetch(`${BASE}/${id}`, {
    method: "DELETE",
    headers: commonHeaders(),
    ...withCredentials,
  });
  if (res.status === 401 || res.status === 403) {
    await jsonOr401<void>(res);
    return;
  }
  if (!res.ok) throw new FaqApiError(`HTTP ${res.status}`);
}
